import { useEffect, useRef, useState } from 'react'

const SOUNDCLOUD_API_URL = 'https://w.soundcloud.com/player/api.js'
const SOUNDCLOUD_API_TIMEOUT = 8000
const WIDGET_CHECK_INTERVAL = 50
const READY_EVENT_TIMEOUT = 1200
const POSITION_UPDATE_INTERVAL = 100

const PLAYER_CONFIG = {
  auto_play: false,
  buying: false,
  sharing: false,
  download: false,
  show_artwork: false,
  show_comments: false,
  show_playcount: false,
  show_user: false,
  visual: false
}

// SoundCloud Widget API types
interface SoundCloudWidget {
  bind: (event: string, callback: () => void) => void
  unbind: (event: string) => void
  load: (url: string, options?: any) => void
  play: () => void
  pause: () => void
  toggle: () => void
  seekTo: (milliseconds: number) => void
  setVolume: (volume: number) => void
  next: () => void
  prev: () => void
  skip: (index: number) => void
  getPosition: (callback: (position: number) => void) => void
  getDuration: (callback: (duration: number) => void) => void
  getVolume: (callback: (volume: number) => void) => void
  getSounds: (callback: (sounds: any[]) => void) => void
  getCurrentSound: (callback: (sound: any) => void) => void
  getCurrentSoundIndex: (callback: (index: number) => void) => void
  isPaused: (callback: (paused: boolean) => void) => void
}

declare global {
  interface Window {
    SC: {
      Widget: {
        (iframe: HTMLIFrameElement): SoundCloudWidget
        Events: {
          READY: string
          PLAY: string
          PAUSE: string
          FINISH: string
          SEEK: string
          LOAD_PROGRESS: string
          PLAY_PROGRESS: string
          ERROR: string
        }
      }
    }
  }
}

interface UseSoundCloudPlayerProps {
  onReady?: () => void
  onPlay?: () => void
  onPause?: () => void
  onFinish?: () => void
  onError?: () => void
  finalVolume: number
}

export function useSoundCloudPlayer({
  onReady,
  onPlay,
  onPause,
  onFinish,
  onError,
  finalVolume
}: UseSoundCloudPlayerProps) {
  const [isReady, setIsReady] = useState(false)
  const [currentPosition, setCurrentPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const widgetRef = useRef<SoundCloudWidget | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const hasCalledReady = useRef(false)

  const loadSoundCloudAPI = () => {
    return new Promise<void>((resolve) => {
      try {
        if (window.SC && typeof window.SC.Widget === 'function') {
          resolve()
          return
        }
      } catch (e) {
      }
      
      const existingScript = document.getElementById('soundcloud-widget-api')
      if (existingScript) {
        const checkInterval = setInterval(() => {
          try {
            if (window.SC && typeof window.SC.Widget === 'function') {
              clearInterval(checkInterval)
              resolve()
            }
          } catch (e) {
            // Still loading
          }
        }, 25)
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve()
        }, SOUNDCLOUD_API_TIMEOUT)
        return
      }
      
      const script = document.createElement('script')
      script.id = 'soundcloud-widget-api'
      script.src = SOUNDCLOUD_API_URL
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => resolve()
      document.body.appendChild(script)
    })
  }

  const initializeWidget = async (iframe: HTMLIFrameElement) => {
    await loadSoundCloudAPI()
    
    const attemptWidgetCreation = () => {
      if (!window.SC?.Widget) return false
      
      try {
        const widget = window.SC.Widget(iframe)
        widgetRef.current = widget

        const handleReady = () => {
          if (hasCalledReady.current) return
          hasCalledReady.current = true
          setIsReady(true)
          onReady?.()
          widget.setVolume(finalVolume)
        }

        widget.bind(window.SC.Widget.Events.READY, handleReady)

        widget.bind(window.SC.Widget.Events.PLAY, () => {
          onPlay?.()
          widget.setVolume(finalVolume)
          widget.getDuration((d) => setDuration(d / 1000))
          
          if (intervalRef.current) clearInterval(intervalRef.current)
          intervalRef.current = setInterval(() => {
            widget.getPosition((pos) => setCurrentPosition(pos / 1000))
          }, POSITION_UPDATE_INTERVAL)
        })

        widget.bind(window.SC.Widget.Events.PAUSE, () => {
          onPause?.()
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        })

        widget.bind(window.SC.Widget.Events.FINISH, () => {
          onFinish?.()
          setCurrentPosition(0)
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
        })

        widget.bind(window.SC.Widget.Events.ERROR, () => onError?.())
        
        setTimeout(() => {
          if (hasCalledReady.current) return
          widget.isPaused(() => handleReady())
        }, READY_EVENT_TIMEOUT)
        
        return true
      } catch (error) {
        console.error('Error creating SoundCloud widget:', error)
        return false
      }
    }
    
    if (attemptWidgetCreation()) return
    
    const checkWidget = setInterval(() => {
      if (attemptWidgetCreation()) {
        clearInterval(checkWidget)
      }
    }, WIDGET_CHECK_INTERVAL)
    
    setTimeout(() => clearInterval(checkWidget), 2000)
  }

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (widgetRef.current && isReady) {
      widgetRef.current.setVolume(finalVolume)
    }
  }, [finalVolume, isReady])

  const loadTrack = (url: string) => {
    if (!widgetRef.current || !isReady) return
    
    widgetRef.current.load(url, PLAYER_CONFIG)
    setCurrentPosition(0)
  }

  const play = () => {
    if (widgetRef.current && isReady) {
      widgetRef.current.play()
    }
  }

  const pause = () => {
    if (widgetRef.current && isReady) {
      widgetRef.current.pause()
    }
  }

  const toggle = () => {
    if (widgetRef.current && isReady) {
      widgetRef.current.toggle()
    }
  }

  const seekTo = (seconds: number) => {
    if (widgetRef.current && isReady) {
      widgetRef.current.seekTo(seconds * 1000)
    }
  }

  return {
    isReady,
    currentPosition,
    duration,
    initializeWidget,
    loadTrack,
    play,
    pause,
    toggle,
    seekTo
  }
}